import { Children, type ReactNode, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import Popup from 'reactjs-popup';
import styles from './BaseEllipsisMenu.module.css';

let activeMenu: { key: object; close: () => void } | null = null;

export type BaseEllipsisMenuProps = {
  options?: Array<{ component?: ReactNode }>;
  open?: boolean;
  closeOption?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  children?: ReactNode;
  closeOnScroll?: boolean;
  ariaLabel?: string;
} & {};

/**
 * Renders a configurable ellipsis-triggered popup menu for action lists.
 *
 * @param props - Menu configuration, optional controlled open state, and trigger label.
 * @returns The ellipsis trigger and popup content, or null when the menu has no items.
 */
export default function BaseEllipsisMenu({
  options = [],
  open,
  closeOption = false,
  onOpen,
  onClose,
  children = null,
  closeOnScroll = false,
  ariaLabel = 'Open actions menu',
}: BaseEllipsisMenuProps): JSX.Element | null {
  const popupRef = useRef<{
    open: () => void;
    close: () => void;
    toggle: () => void;
  } | null>(null);
  const menuKeyRef = useRef<object>({});

  useEffect(() => {
    if (typeof open !== 'boolean') return;
    const popup = popupRef.current;
    if (!popup) return;
    if (open) popup.open?.();
    else popup.close?.();
  }, [open]);

  useEffect(() => {
    const close = () => {
      const popup = popupRef.current;
      if (popup) popup.close?.();
    };
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('resize', close);
      if (activeMenu?.key === menuKeyRef.current) activeMenu = null;
    };
  }, []);

  useEffect(() => {
    if (!closeOnScroll) return undefined;
    const close = () => {
      const popup = popupRef.current;
      if (popup) popup.close?.();
    };
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [closeOnScroll]);

  const content = [
    ...options
      .map((option) => option?.component)
      .filter((item): item is ReactNode => Boolean(item)),
    ...Children.toArray(children),
  ] as ReactNode[];

  if (content.length === 0 && !closeOption) return null;

  const handleOpen = () => {
    if (activeMenu?.key !== menuKeyRef.current) activeMenu?.close();
    activeMenu = {
      key: menuKeyRef.current,
      close: () => popupRef.current?.close?.(),
    };
    onOpen?.();
  };

  const handleClose = () => {
    if (activeMenu?.key === menuKeyRef.current) activeMenu = null;
    onClose?.();
  };

  const trigger = (
    <Button
      type="button"
      variant="primary"
      className={styles.trigger}
      aria-label={ariaLabel}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <span className={styles.dots} aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    </Button>
  );

  return (
    <Popup
      ref={popupRef}
      on="click"
      trigger={trigger}
      onOpen={handleOpen}
      onClose={handleClose}
      position={['bottom right', 'bottom left', 'top right', 'top left']}
      closeOnDocumentClick
      closeOnEscape
      keepTooltipInside
      arrow={false}
    >
      <div
        className={styles.menu}
        role="menu"
        onClick={(event) => {
          event.stopPropagation();
          popupRef.current?.close?.();
        }}
      >
        {content.map((item, index) => (
          <div className={styles.item} role="menuitem" key={index}>
            {item}
          </div>
        ))}
        {closeOption && (
          <button
            type="button"
            role="menuitem"
            className={styles.close}
            onClick={() => popupRef.current?.close?.()}
          >
            Close
          </button>
        )}
      </div>
    </Popup>
  );
}
